import { Checkbox, FormControlLabel, FormGroup, Grid } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { FormikErrors, FormikHandlers, FormikState } from 'formik';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useAuth } from '../session/InternalAuthProvider';
import { AccountData, OrderPageData } from '../api';
import { useLanguage } from '../common/GeneralUtils';
import { WorkshopCircle } from '../common/ui/WorkshopCircle';
import { getOrderPages } from '../utils/AccountUtils';
import { OrderPageLocationGroup, useOrderPageLocationGroups } from '../utils/useOrderPageLocationGroups';
import { SortableOrderPageItem } from './SortableOrderPageItem';

// I dont have a clue how this chatgpt type sorcery works
type NonFunctionPropertyNames<T> = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  [K in keyof T]: T[K] extends Function ? never : K;
}[keyof T];

type NonFunctionProperties<T> = Pick<T, NonFunctionPropertyNames<T>>;

type NestedKeyOf<ObjectType extends object> = {
  [Key in keyof NonFunctionProperties<ObjectType> &
    (string | number)]: NonFunctionProperties<ObjectType>[Key] extends object
    ? `${Key}` | `${Key}.${NestedKeyOf<NonFunctionProperties<ObjectType>[Key]>}`
    : `${Key}`;
}[keyof NonFunctionProperties<ObjectType> & (string | number)];

type OrderPagesMultiSelectProps<T extends object> = {
  values: T;
  errors: FormikErrors<T>;
  touched: FormikState<T>['touched'];
  handleChange: FormikHandlers['handleChange'];
  orderPagesFieldName: NestedKeyOf<T>;
  account: AccountData;
  exceptOrderPageId?: string;
  orderable?: boolean;
  setFieldValue?: (field: string, value: any) => void;
  locationId?: string | null;
};

export const OrderPagesMultiSelect = <T extends Record<string, any>>({
  values,
  errors,
  touched,
  handleChange,
  orderPagesFieldName,
  account,
  exceptOrderPageId,
  orderable,
  setFieldValue,
  locationId,
}: OrderPagesMultiSelectProps<T>) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const language = useLanguage();

  const getNestedValue = <T extends Record<string, any>>(obj: T, key: string): any => {
    return key.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
  };
  const fieldValue: string[] = getNestedValue(values, orderPagesFieldName);
  const fieldError = getNestedValue(errors, orderPagesFieldName);
  const fieldTouched = getNestedValue(touched, orderPagesFieldName);
  const orderPages = getOrderPages(authState.account).filter((x: OrderPageData) => x.id !== exceptOrderPageId) || [];

  const {
    groupByLocation,
    groupPagesByLocation: groupAllPagesByLocation,
    locationLabel,
  } = useOrderPageLocationGroups(account);

  const groupPagesByLocation = (pages: OrderPageData[]) =>
    groupAllPagesByLocation(pages).filter(
      (group) => !locationId || !group.location || group.location.id === locationId,
    );

  const groupLabel = (group: OrderPageLocationGroup) =>
    !group.location && locationId ? t('locationPicker.withoutLocation') : locationLabel(group.location);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  if (orderable && setFieldValue) {
    const fieldName = String(orderPagesFieldName);
    const orderPagesById = new Map(orderPages.map((p) => [p.id, p]));
    const selectedPages = fieldValue.map((id) => orderPagesById.get(id)).filter(Boolean) as OrderPageData[];
    const unselectedPages = orderPages.filter((p) => !fieldValue.includes(p.id));

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = fieldValue.indexOf(active.id as string);
        const newIndex = fieldValue.indexOf(over.id as string);
        setFieldValue(fieldName, arrayMove(fieldValue, oldIndex, newIndex));
      }
    };

    const handleToggle = (pageId: string, wasChecked: boolean) => {
      if (wasChecked) {
        setFieldValue(
          fieldName,
          fieldValue.filter((id) => id !== pageId),
        );
      } else {
        setFieldValue(fieldName, [...fieldValue, pageId]);
      }
    };

    const renderUnselectedCheckbox = (page: OrderPageData) => (
      <FormControlLabel
        key={page.id}
        control={<Checkbox checked={false} onChange={() => handleToggle(page.id, false)} />}
        label={
          <Grid container flexWrap="nowrap" gap={1} alignContent="baseline">
            <Grid pt={0.4}>
              <WorkshopCircle account={account} orderPageId={page.id} language={language} />
            </Grid>
            <Typography>{page.label[language]}</Typography>
          </Grid>
        }
      />
    );

    return (
      <Grid container flexDirection="column">
        <FormGroup>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={fieldValue} strategy={verticalListSortingStrategy}>
              {selectedPages.map((page) => (
                <SortableOrderPageItem
                  key={page.id}
                  id={page.id}
                  page={page}
                  account={account}
                  language={language}
                  checked
                  onToggle={handleToggle}
                />
              ))}
            </SortableContext>
          </DndContext>
          {groupByLocation ? (
            groupPagesByLocation(unselectedPages).map((group, idx) => (
              <Grid item key={group.location?.id || 'other'} mt={idx === 0 ? 0.5 : 0.75}>
                <Typography variant="caption" color="text.secondary">
                  {groupLabel(group)}
                </Typography>
                <FormGroup>{group.pages.map(renderUnselectedCheckbox)}</FormGroup>
              </Grid>
            ))
          ) : (
            <>{unselectedPages.map(renderUnselectedCheckbox)}</>
          )}
        </FormGroup>
        {fieldError && fieldTouched && <Typography color="error">{fieldError}</Typography>}
      </Grid>
    );
  }

  const renderCheckbox = (page: OrderPageData) => (
    <FormControlLabel
      key={page.id}
      control={
        <Checkbox
          name={String(orderPagesFieldName)}
          value={page.id}
          checked={fieldValue.includes(page.id)}
          onChange={handleChange}
        />
      }
      label={
        <Grid container flexWrap="nowrap" gap={1} alignContent="baseline">
          <Grid pt={0.4}>
            <WorkshopCircle account={account} orderPageId={page.id} language={language} />
          </Grid>
          <Typography>{page.label[language]}</Typography>
        </Grid>
      }
    />
  );

  return (
    <Grid container flexDirection="column">
      {groupByLocation ? (
        groupPagesByLocation(orderPages).map((group, idx) => (
          <Grid item key={group.location?.id || 'other'} mt={idx === 0 ? 0 : 0.75}>
            <Typography variant="caption" color="text.secondary">
              {groupLabel(group)}
            </Typography>
            <FormGroup>{group.pages.map(renderCheckbox)}</FormGroup>
          </Grid>
        ))
      ) : (
        <FormGroup>{orderPages.map(renderCheckbox)}</FormGroup>
      )}
      {fieldError && fieldTouched && <Typography color="error">{fieldError}</Typography>}
    </Grid>
  );
};
